import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import Menu from "./Menu";
import Personalizaciones from "./Personalizaciones";
import Usuarios from "./Usuarios";
import Locales from "./Locales";
import CalendarioEntregas from "../../components/CalendarioEntregas";
import { parseFechaLocal, parseFechaHoraLocal, formatPorcionesOCantidad } from "../../utils/pedidoDisplay";

export default function PanelJefe() {
  const { logout, user } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [vista, setVista] = useState("resumen");
  const [periodo, setPeriodo] = useState("hoy");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  useEffect(() => {
    cargarPedidos();
  }, []);

  const cargarPedidos = async () => {
    try {
      const res = await api.get("/pedidos");
      setPedidos(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const hoy = new Date();

  const filtrarPorRango = (lista, inicio, fin) => {
    return lista.filter((p) => {
      const fecha = parseFechaLocal(p.fecha_entrega);
      return fecha >= inicio && fecha <= fin;
    });
  };

  const pedidosFiltrados = pedidos.filter((p) => {
    const fecha = parseFechaLocal(p.fecha_entrega);
    if (!fecha) return false;

    if (periodo === "hoy") {
      return fecha.toDateString() === hoy.toDateString();
    }

    if (periodo === "semana") {
      const hace7Dias = new Date();
      hace7Dias.setDate(hoy.getDate() - 7);
      return fecha >= hace7Dias && fecha <= hoy;
    }

    if (periodo === "mes") {
      return (
        fecha.getMonth() === hoy.getMonth() &&
        fecha.getFullYear() === hoy.getFullYear()
      );
    }

    if (periodo === "rango") {
      if (!fechaInicio || !fechaFin) return true;

      const inicio = parseFechaLocal(fechaInicio);
      const fin = parseFechaLocal(fechaFin);
      fin.setHours(23, 59, 59, 999);

      return fecha >= inicio && fecha <= fin;
    }

    return true;
  });

  // Calcula el periodo anterior equivalente para comparacion
  const pedidosPeriodoAnterior = (() => {
    if (periodo === "hoy") {
      const ayer = new Date();
      ayer.setDate(hoy.getDate() - 1);
      ayer.setHours(0, 0, 0, 0);
      const finAyer = new Date(ayer);
      finAyer.setHours(23, 59, 59, 999);
      return filtrarPorRango(pedidos, ayer, finAyer);
    }

    if (periodo === "semana") {
      const inicioAnterior = new Date();
      inicioAnterior.setDate(hoy.getDate() - 14);
      const finAnterior = new Date();
      finAnterior.setDate(hoy.getDate() - 7);
      return filtrarPorRango(pedidos, inicioAnterior, finAnterior);
    }

    if (periodo === "mes") {
      const mesAnterior = hoy.getMonth() === 0 ? 11 : hoy.getMonth() - 1;
      const anioMesAnterior = hoy.getMonth() === 0 ? hoy.getFullYear() - 1 : hoy.getFullYear();
      return pedidos.filter((p) => {
        const fecha = parseFechaLocal(p.fecha_entrega);
        if (!fecha) return false;
        return fecha.getMonth() === mesAnterior && fecha.getFullYear() === anioMesAnterior;
      });
    }

    return null;
  })();

  const calcularCambio = (actual, anterior) => {
    if (anterior === 0) return actual > 0 ? 100 : 0;
    return Math.round(((actual - anterior) / anterior) * 100);
  };

  const pedidosOrdenados = [...pedidosFiltrados].sort((a, b) => {
    const fechaA = parseFechaHoraLocal(a.fecha_entrega, a.hora_entrega);
    const fechaB = parseFechaHoraLocal(b.fecha_entrega, b.hora_entrega);
    return fechaA - fechaB;
  });

  const cobrado = pedidosFiltrados.reduce((a, b) => a + parseInt(b.abono || 0), 0);
  const saldos = pedidosFiltrados.reduce((a, b) => a + parseInt(b.saldo || 0), 0);
  const pendientes = pedidosFiltrados.filter((p) => p.estado === "pendiente").length;
  const listos = pedidosFiltrados.filter((p) => p.estado === "listo").length;

  const cobradoAnterior = pedidosPeriodoAnterior
    ? pedidosPeriodoAnterior.reduce((a, b) => a + parseInt(b.abono || 0), 0)
    : null;

  const cambioTotalPedidos = pedidosPeriodoAnterior
    ? calcularCambio(pedidosFiltrados.length, pedidosPeriodoAnterior.length)
    : null;
  const cambioCobrado = pedidosPeriodoAnterior
    ? calcularCambio(cobrado, cobradoAnterior)
    : null;

  const labelPeriodoAnterior = {
    hoy: "vs. ayer",
    semana: "vs. semana pasada",
    mes: "vs. mes pasado",
  }[periodo];

  const IndicadorCambio = ({ valor }) => {
    if (valor === null) return null;
    const esPositivo = valor >= 0;
    return (
      <div className={`flex items-center gap-1 text-xs font-medium mt-1 ${esPositivo ? "text-green-600" : "text-red-500"}`}>
        <span>{esPositivo ? "↑" : "↓"}</span>
        <span>{Math.abs(valor)}%</span>
        <span className="text-gray-400 font-normal">{labelPeriodoAnterior}</span>
      </div>
    );
  };

  const menuItems = [
    { key: "resumen", label: "Resumen", icon: "📊" },
    { key: "calendario", label: "Calendario", icon: "📅" },
    { key: "menu", label: "Menú", icon: "🎂" },
    { key: "personalizaciones", label: "Personalización", icon: "✨" },
    { key: "usuarios", label: "Usuarios", icon: "👥" },
    { key: "locales", label: "Locales", icon: "🏬" },
  ];

  return (
    <div className="min-h-screen bg-[#E4E9F2] flex">
      {/* SIDEBAR */}
      <div className="w-64 bg-gradient-to-b from-[#0F1F4D] to-[#1A56DB] flex-shrink-0 flex flex-col">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <img
              src="/logobancher.png"
              alt="Bancher"
              className="w-9 h-9 object-contain rounded-lg bg-white/10 p-1"
            />
            <div>
              <div className="text-white font-bold text-sm leading-tight">Bancher</div>
              <div className="text-blue-300 text-[10px] uppercase tracking-wider">Panel Dueño</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.key}
              onClick={() => setVista(item.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                vista === item.key
                  ? "bg-white/10 text-white shadow-sm border-l-2 border-blue-300"
                  : "text-blue-200/70 hover:bg-white/5 hover:text-white"
              }`}
            >
              <span className="text-base">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 text-blue-200/70 text-xs mb-1">{user?.nombre}</div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-blue-200/70 hover:bg-white/5 hover:text-white transition-all"
          >
            <span className="text-base">🚪</span>
            Salir
          </button>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white border-b border-[#E5E9F0] px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <h1 className="text-lg font-semibold text-gray-900">
            {menuItems.find((m) => m.key === vista)?.label}
          </h1>
        </div>

        <div className="p-6">
          {vista === "resumen" && (
            <div>
              <div className="flex gap-2 mb-4 flex-wrap">
                {[
                  { key: "hoy", label: "Hoy" },
                  { key: "semana", label: "Semana" },
                  { key: "mes", label: "Mes" },
                  { key: "todos", label: "Todos" },
                  { key: "rango", label: "Por fechas" },
                ].map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setPeriodo(item.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      periodo === item.key
                        ? "bg-gradient-to-r from-[#0F1F4D] to-[#1A56DB] text-white shadow-sm"
                        : "bg-white text-gray-600 border border-[#E5E9F0] hover:border-[#1A56DB] hover:text-[#1A56DB]"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {periodo === "rango" && (
                <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3 shadow-sm">
                  <div>
                    <label className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                      Desde
                    </label>
                    <input
                      type="date"
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      className="w-full mt-1 bg-[#E4E9F2] border border-[#E5E9F0] rounded-lg px-3 py-2.5 text-gray-900 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                      Hasta
                    </label>
                    <input
                      type="date"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      className="w-full mt-1 bg-[#E4E9F2] border border-[#E5E9F0] rounded-lg px-3 py-2.5 text-gray-900 text-sm focus:outline-none focus:border-[#1A56DB] focus:ring-1 focus:ring-[#1A56DB]"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 text-center shadow-sm">
                  <div className="text-2xl font-bold text-[#1A56DB]">
                    {pedidosFiltrados.length}
                  </div>
                  <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">
                    Total pedidos
                  </div>
                  <div className="flex justify-center">
                    <IndicadorCambio valor={cambioTotalPedidos} />
                  </div>
                </div>

                <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 text-center shadow-sm">
                  <div className="text-xl font-bold text-green-600">
                    ${cobrado.toLocaleString("es-CL")}
                  </div>
                  <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">
                    Ya cobrado
                  </div>
                  <div className="flex justify-center">
                    <IndicadorCambio valor={cambioCobrado} />
                  </div>
                </div>

                <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 text-center shadow-sm">
                  <div className="text-xl font-bold text-amber-600">
                    ${saldos.toLocaleString("es-CL")}
                  </div>
                  <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">
                    Saldos pendientes
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <div className="text-xl font-bold text-amber-700">
                    {pendientes}
                  </div>
                  <div className="text-xs text-amber-600 mt-1">Pendientes</div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                  <div className="text-xl font-bold text-green-700">{listos}</div>
                  <div className="text-xs text-green-600 mt-1">Listos</div>
                </div>
              </div>

              <div className="bg-white border border-[#E5E9F0] rounded-xl overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-[#E5E9F0] flex items-center justify-between">
                  <h2 className="text-[#1A56DB] font-semibold text-sm uppercase tracking-wider">
                    Pedidos del período
                  </h2>
                  <span className="text-xs text-gray-400">
                    {pedidosFiltrados.length} registros
                  </span>
                </div>

                <div className="divide-y divide-gray-100 max-h-[calc(100vh-390px)] overflow-y-auto">
                  {pedidosOrdenados.length === 0 && (
                    <div className="text-center text-gray-400 py-8 text-sm">
                      Sin pedidos en este período
                    </div>
                  )}

                  {pedidosOrdenados.map((p) => (
                    <div
                      key={p.id}
                      className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-[#E4E9F2] transition-all"
                    >
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-[#1A56DB]">
                            {p.numero_pedido}
                          </span>

                          <span className="text-sm font-medium text-gray-900">
                            {p.cliente_nombre}
                          </span>

                          <span
                            className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              p.local_origen === "sur"
                                ? "bg-blue-50 text-blue-700 border border-blue-200"
                                : "bg-gray-100 text-gray-500 border border-[#E5E9F0]"
                            }`}
                          >
                            {p.local_origen?.toUpperCase()}
                          </span>
                        </div>

                        <div className="text-xs text-gray-400 mt-0.5">
                          🎂 {p.producto} · {formatPorcionesOCantidad(p)} ·{" "}
                          {parseFechaLocal(p.fecha_entrega)?.toLocaleDateString("es-CL", {
                            day: "numeric",
                            month: "short",
                          })}{" "}
                          · {p.hora_entrega?.slice(0, 5)}
                        </div>
                      </div>

                      <div className="text-right min-w-[110px]">
                        <div className="text-sm font-semibold text-gray-900">
                          ${parseInt(p.precio_total).toLocaleString("es-CL")}
                        </div>

                        <div
                          className={`text-xs font-medium ${
                            parseInt(p.saldo) > 0
                              ? "text-amber-600"
                              : "text-green-600"
                          }`}
                        >
                          {parseInt(p.saldo) > 0
                            ? `Saldo: $${parseInt(p.saldo).toLocaleString("es-CL")}`
                            : "✓ Pagado"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {vista === "calendario" && (
            <div className="bg-white border border-[#E5E9F0] rounded-xl p-4 shadow-sm">
              <CalendarioEntregas pedidos={pedidos} />
            </div>
          )}
          {vista === "menu" && <Menu />}
          {vista === "personalizaciones" && <Personalizaciones />}
          {vista === "usuarios" && <Usuarios />}
          {vista === "locales" && <Locales />}
        </div>
      </div>
    </div>
  );
}
